<section class="log-test" aria-labelledby="log-test-title">
  <header>
    <span class="log-test__badge">Synthetic tests</span>
    <h2 id="log-test-title">Sentry log tests</h2>
    <p>
      Generate clearly marked test logs without reading or changing client
      records.
    </p>
    <p>
      These scenarios test log generation and delivery. They do not interrupt
      real workflows.
    </p>
  </header>

  <div class="log-test__run">
    <div>
      <h3>Find this run in Sentry</h3>
      <p>
        Filter Logs by <code>source = logging_test</code> and this
        <code>test_run_id</code>:
      </p>
      <code class="log-test__id">{{ testRunId() }}</code>
      <p>
        Exclude <code>source = logging_test</code> when investigating real
        incidents.
      </p>
    </div>
    <button type="button" (click)="newRun()" [disabled]="busy()">
      New test run
    </button>
  </div>

  @if (!enabled) {
    <p role="status">
      Structured logging is disabled. Enable structured log collection to run
      these tests.
    </p>
  }

  <p>
    Blocked attempts, uncertain outcomes, and recoveries have different
    meanings. A recovery event is not another error.
  </p>
  <p-accordion [multiple]="true" class="log-test__scenarios">
    <p-accordion-panel value="error-capture">
      <p-accordion-header>
        <span class="log-test__group-heading">
          <span class="log-test__group-title">Error capture verification</span>
          <span class="log-test__group-count">3 probes</span>
        </span>
      </p-accordion-header>
      <p-accordion-content>
        <p>
          These probes intentionally raise harmless errors through Angular or
          the browser. They create error events in Sentry Issues, separately
          from the log scenarios below. They do not read or change client
          records.
        </p>
        <p>
          Find them with <code>source = sentry_test</code> and the run ID above.
          Triggered means the error was raised; it does not confirm delivery.
          Errors may also appear in the browser console.
        </p>
        <p-accordion [multiple]="true" class="log-test__scenarios">
          <p-accordion-panel value="angular-error">
            <p-accordion-header>Angular event handler</p-accordion-header>
            <p-accordion-content>
              <p>
                In real use, a component event handler or view throws and
                Angular catches it. This probe verifies the configured Angular
                error handler reports the exception.
              </p>
              <p>Expected scenario: <code>angular_error</code>.</p>
              <button type="button" (click)="testAngularError()">
                Test Angular error handler
              </button>
            </p-accordion-content>
          </p-accordion-panel>
          <p-accordion-panel value="browser-error">
            <p-accordion-header>Browser error</p-accordion-header>
            <p-accordion-content>
              <p>
                In real use, a task outside Angular throws without a handler.
                This probe verifies Sentry's global browser error capture.
              </p>
              <p>Expected scenario: <code>browser_error</code>.</p>
              <button type="button" (click)="testBrowserError('browser_error')">
                Test browser error
              </button>
            </p-accordion-content>
          </p-accordion-panel>
          <p-accordion-panel value="unhandled-rejection">
            <p-accordion-header>Unhandled rejection</p-accordion-header>
            <p-accordion-content>
              <p>
                In real use, an asynchronous operation outside Angular rejects
                without a catch handler. This probe verifies Sentry's global
                rejection capture.
              </p>
              <p>Expected scenario: <code>unhandled_rejection</code>.</p>
              <button
                type="button"
                (click)="testBrowserError('unhandled_rejection')"
              >
                Test unhandled rejection
              </button>
            </p-accordion-content>
          </p-accordion-panel>
        </p-accordion>
        @if (errorProbeStatus()) {
          <p role="status">{{ errorProbeStatus() }}</p>
        }
      </p-accordion-content>
    </p-accordion-panel>
  </p-accordion>

  <p-accordion [multiple]="true">
    <p-accordion-panel value="trace-capture">
      <p-accordion-header>
        <span class="log-test__group-heading">
          <span class="log-test__group-title">Trace verification</span>
          <span class="log-test__group-count">1 probe</span>
        </span>
      </p-accordion-header>
      <p-accordion-content>
        <p>
          In real use, a page navigation or request produces timing spans. This
          probe sends a synthetic trace with a simulated HTTP child span. It
          makes no API request and reads or changes no client records.
        </p>
        <p>
          Find the returned trace ID in Sentry's Traces or Performance view,
          rather than Issues. Its name is
          <code>Sentry verification: trace filtering</code>, with
          <code>source = sentry_trace_test</code> and the run ID above. The HTTP
          span should show <code>GET /case/:id</code> and status 503. The
          synthetic GUID
          <code>00000000-0000-4000-8000-000000000003</code> should remain as
          <code>client_guid</code>. Internal <code>case_id</code>,
          <code>conversation_id</code> and <code>run_id</code> should also
          remain. Account details, synthetic private fields and raw URL
          parameters should be absent. A transport acknowledgement confirms
          ingestion acceptance; inspect the trace to confirm indexing and its
          retained fields.
        </p>
        <button
          type="button"
          [disabled]="traceProbeBusy()"
          (click)="testTrace()"
        >
          Test trace filtering
        </button>
        @if (traceProbeStatus()) {
          <p role="status">{{ traceProbeStatus() }}</p>
        }
      </p-accordion-content>
    </p-accordion-panel>
  </p-accordion>

  <p-accordion [multiple]="true">
    <p-accordion-panel value="replay-verification">
      <p-accordion-header>
        <span class="log-test__group-heading">
          <span class="log-test__group-title">Replay verification</span>
          <span class="log-test__group-count">1 probe</span>
        </span>
      </p-accordion-header>
      <p-accordion-content>
        <p>
          In real use, replay records a masked sequence of page interactions.
          Account, credit-report and financial-snapshot content is blocked. This
          probe records synthetic text, input and link changes for about six
          seconds. It reads or changes no client record.
        </p>
        <p>
          Find the returned replay ID in Sentry's Replays view, with
          <code>source = sentry_replay_test</code> and this run ID. Text/input
          values should be masked, the synthetic account section blocked, and
          link query data absent. Approved internal references may remain.
          Playback should still work. HTTP acceptance does not by itself confirm
          playback or retained-field correctness.
        </p>
        <button
          type="button"
          [disabled]="replayProbeBusy()"
          (click)="testReplay()"
        >
          Test replay filtering
        </button>
        @if (replayProbeVisible()) {
          <div class="p-3" data-case-id="00000000-0000-4000-8000-000000000004">
            <p>SENTRY_SYNTHETIC_PRIVATE_REPLAY</p>
            <input
              aria-label="Synthetic replay input"
              value="SENTRY_SYNTHETIC_PRIVATE_REPLAY"
              data-account-number="SENTRY_SYNTHETIC_PRIVATE_REPLAY"
            />
            <a
              href="/case/00000000-0000-4000-8000-000000000004?account=SENTRY_SYNTHETIC_PRIVATE_REPLAY"
              >Synthetic case link</a
            >
            <div class="bn-account-detail-dialog" data-sentry-block>
              SENTRY_SYNTHETIC_ACCOUNT_REPLAY
            </div>
          </div>
        }
        @if (replayProbeStatus()) {
          <p role="status">{{ replayProbeStatus() }}</p>
        }
      </p-accordion-content>
    </p-accordion-panel>
  </p-accordion>

  <p-accordion [multiple]="true">
    @for (group of scenarioGroups; track group.id) {
      <p-accordion-panel [value]="group.id">
        <p-accordion-header>
          <span class="log-test__group-heading">
            <span class="log-test__group-title">{{ group.title }}</span>
            <span class="log-test__group-count"
              >{{ group.scenarios.length }}
              {{
                group.scenarios.length === 1 ? 'scenario' : 'scenarios'
              }}</span
            >
          </span>
        </p-accordion-header>
        <p-accordion-content>
          <p>{{ group.description }}</p>
          <p-accordion class="log-test__scenarios" [multiple]="true">
            @for (scenario of group.scenarios; track scenario.id) {
              <p-accordion-panel [value]="scenario.id">
                <p-accordion-header>
                  <span class="log-test__scenario-heading">
                    <span class="log-test__scenario-title">{{
                      scenario.label
                    }}</span>
                    <span class="log-test__outcome">{{
                      scenario.outcome
                    }}</span>
                  </span>
                </p-accordion-header>
                <p-accordion-content>
                  <article [attr.aria-label]="scenario.label">
                    <p>{{ scenario.detail }}</p>
                    <dl>
                      <dt>In real use</dt>
                      <dd>{{ scenario.realCause }}</dd>
                      <dt>How to interpret it</dt>
                      <dd>{{ scenario.meaning }}</dd>
                    </dl>
                    @if (scenario.coverageNote) {
                      <p class="log-test__coverage">
                        {{ scenario.coverageNote }}
                      </p>
                    }
                    <details class="log-test__details">
                      <summary>Expected logs</summary>
                      <ul>
                        @for (event of scenario.events; track event.name) {
                          <li>
                            <code>{{ event.name }}</code>
                            <span>· {{ event.level }}</span>
                          </li>
                        }
                      </ul>
                    </details>
                    <button
                      type="button"
                      [disabled]="busy() || !enabled"
                      (click)="run(scenario)"
                    >
                      Test {{ scenario.label.toLowerCase() }}
                    </button>
                    @if (activeScenario() === scenario.id) {
                      <p role="status" aria-live="polite">
                        @if (busy()) {
                          Generating test events and flushing the Sentry queue…
                        } @else if (results()[0]; as result) {
                          {{ deliveryMessages[result.flush] }}
                        }
                      </p>
                    }
                  </article>
                </p-accordion-content>
              </p-accordion-panel>
            }
          </p-accordion>
        </p-accordion-content>
      </p-accordion-panel>
    }
  </p-accordion>

  @if (results().length) {
    <section aria-label="Test run history">
      @for (result of results(); track $index) {
        <div class="log-test__result">
          <h3>{{ result.label }}</h3>
          <p>{{ deliveryMessages[result.flush] }}</p>
          <ul>
            @for (event of result.events; track $index) {
              <li>
                <code>{{ event }}</code>
              </li>
            }
          </ul>
        </div>
      }
    </section>
  }
</section>
